import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  IconButton,
  MenuItem,
  CircularProgress,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Payment as PaymentIcon,
  CheckCircle as CompleteIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import billingService, { 
  Payroll, 
  PayrollStatus,
  BillingPeriod,
  ProcessPaymentRequest 
} from '../../services/newBillingService';

interface PayrollViewProps {
  payrolls: Payroll[];
  billingPeriod: BillingPeriod;
  onRefresh: () => void;
}

const PayrollView: React.FC<PayrollViewProps> = ({
  payrolls,
  billingPeriod,
  onRefresh
}) => {
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [processingPayroll, setProcessingPayroll] = useState<Payroll | null>(null);
  const [completingPayroll, setCompletingPayroll] = useState<Payroll | null>(null);
  const [paymentForm, setPaymentForm] = useState<ProcessPaymentRequest>({
    paymentMethod: '',
    paymentReference: '',
    scheduledPaymentDate: '',
    notes: ''
  });
  const [completeForm, setCompleteForm] = useState({
    transactionId: '',
    actualPaymentDate: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusColor = (status: PayrollStatus): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case PayrollStatus.Pending: return 'warning';
      case PayrollStatus.Scheduled: return 'info';
      case PayrollStatus.Processing: return 'secondary';
      case PayrollStatus.Completed: return 'success';
      case PayrollStatus.Failed: return 'error';
      case PayrollStatus.Cancelled: return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status: PayrollStatus): string => {
    switch (status) {
      case PayrollStatus.Pending: return 'Pending';
      case PayrollStatus.Scheduled: return 'Scheduled';
      case PayrollStatus.Processing: return 'Processing';
      case PayrollStatus.Completed: return 'Completed';
      case PayrollStatus.Failed: return 'Failed';
      case PayrollStatus.Cancelled: return 'Cancelled';
      default: return 'Unknown';
    }
  };

  const toggleRowExpansion = (payrollId: number) => {
    setExpandedRows(prev => 
      prev.includes(payrollId) 
        ? prev.filter(id => id !== payrollId)
        : [...prev, payrollId]
    );
  };

  const handleProcessPayment = (payroll: Payroll) => {
    setProcessingPayroll(payroll);
    setPaymentForm({
      paymentMethod: '',
      paymentReference: '',
      scheduledPaymentDate: '',
      notes: ''
    });
    setError(null);
  };

  const handleCompletePayment = (payroll: Payroll) => {
    setCompletingPayroll(payroll);
    setCompleteForm({
      transactionId: '',
      actualPaymentDate: new Date().toISOString().split('T')[0]
    });
    setError(null);
  };

  const submitProcessPayment = async () => {
    if (!processingPayroll) return;

    try {
      setSubmitting(true);
      setError(null);

      await billingService.payroll.processPayment(processingPayroll.id, paymentForm);
      setProcessingPayroll(null);
      onRefresh();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitCompletePayment = async () => {
    if (!completingPayroll) return;

    try {
      setSubmitting(true);
      setError(null);

      await billingService.payroll.completePayment(
        completingPayroll.id, 
        completeForm.transactionId,
        completeForm.actualPaymentDate
      );
      setCompletingPayroll(null);
      onRefresh();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canProcessPayment = (payroll: Payroll): boolean => {
    return payroll.status === PayrollStatus.Pending;
  };

  const canCompletePayment = (payroll: Payroll): boolean => {
    return payroll.status === PayrollStatus.Scheduled || payroll.status === PayrollStatus.Processing;
  };

  if (payrolls.length === 0) {
    return (
      <Box>
        <Alert severity="info">
          No payroll records found for this billing period.
        </Alert>
      </Box>
    );
  }

  const pendingPayrolls = payrolls.filter(p => p.status === PayrollStatus.Pending).length;
  const scheduledPayrolls = payrolls.filter(p => p.status === PayrollStatus.Scheduled).length;
  const completedPayrolls = payrolls.filter(p => p.status === PayrollStatus.Completed).length;
  const totalGrossAmount = payrolls.reduce((sum, payroll) => sum + payroll.grossAmount, 0);
  const totalNetAmount = payrolls.reduce((sum, payroll) => sum + payroll.netAmount, 0);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary */}
      <Box mb={3} display="flex" gap={4} flexWrap="wrap">
        <Box>
          <Typography variant="h6">
            Total Payrolls: {payrolls.length}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body1" color="warning.main">
            Pending: {pendingPayrolls}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body1" color="info.main">
            Scheduled: {scheduledPayrolls}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body1" color="success.main">
            Completed: {completedPayrolls}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body1">
            Total Net: {formatCurrency(totalNetAmount)}
          </Typography>
        </Box>
      </Box>

      {/* Payrolls Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Payroll Number</TableCell>
              <TableCell>Staff</TableCell>
              <TableCell>Gross Amount</TableCell>
              <TableCell>Net Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Scheduled Date</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payrolls.map((payroll) => (
              <React.Fragment key={payroll.id}>
                <TableRow>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleRowExpansion(payroll.id)}
                    >
                      {expandedRows.includes(payroll.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {payroll.payrollNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="subtitle2">
                        {payroll.staffName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {payroll.employeeId}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatCurrency(payroll.grossAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(payroll.netAmount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(payroll.status)}
                      color={getStatusColor(payroll.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payroll.paymentMethod || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payroll.scheduledPaymentDate ? 
                        format(new Date(payroll.scheduledPaymentDate), 'MMM dd, yyyy') : 
                        '-'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={1}>
                      {canProcessPayment(payroll) && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<PaymentIcon />}
                          onClick={() => handleProcessPayment(payroll)}
                        >
                          Process
                        </Button>
                      )}
                      {canCompletePayment(payroll) && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CompleteIcon />}
                          onClick={() => handleCompletePayment(payroll)}
                        >
                          Complete
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={expandedRows.includes(payroll.id)} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Payroll Details
                        </Typography>
                        <Box display="flex" gap={4} flexWrap="wrap" mb={2}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Created
                            </Typography>
                            <Typography variant="body2">
                              {format(new Date(payroll.createdAt), 'MMM dd, yyyy HH:mm')}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Total Deductions
                            </Typography>
                            <Typography variant="body2">
                              {formatCurrency(payroll.totalDeductions)}
                            </Typography>
                          </Box>
                          {payroll.paymentReference && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Payment Reference
                              </Typography>
                              <Typography variant="body2">
                                {payroll.paymentReference}
                              </Typography>
                            </Box>
                          )}
                          {payroll.transactionId && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Transaction ID
                              </Typography>
                              <Typography variant="body2">
                                {payroll.transactionId}
                              </Typography>
                            </Box>
                          )}
                          {payroll.actualPaymentDate && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Actual Payment Date
                              </Typography>
                              <Typography variant="body2">
                                {format(new Date(payroll.actualPaymentDate), 'MMM dd, yyyy')}
                              </Typography>
                            </Box>
                          )}
                          {payroll.processedAt && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Processed At
                              </Typography>
                              <Typography variant="body2">
                                {format(new Date(payroll.processedAt), 'MMM dd, yyyy HH:mm')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        {payroll.notes && (
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              Notes
                            </Typography>
                            <Typography variant="body2">
                              {payroll.notes}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Process Payment Dialog */}
      <Dialog 
        open={!!processingPayroll} 
        onClose={() => setProcessingPayroll(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Process Payment - {processingPayroll?.payrollNumber}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="body1" gutterBottom>
                Staff: {processingPayroll?.staffName}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Net Amount: {processingPayroll && formatCurrency(processingPayroll.netAmount)}
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Payment Method"
              select
              value={paymentForm.paymentMethod}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
              required
            >
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="check">Check</MenuItem>
              <MenuItem value="direct_deposit">Direct Deposit</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Payment Reference"
              value={paymentForm.paymentReference}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentReference: e.target.value })}
              placeholder="e.g., Check number, reference ID"
            />
            <TextField
              fullWidth
              label="Scheduled Payment Date"
              type="date"
              value={paymentForm.scheduledPaymentDate}
              onChange={(e) => setPaymentForm({ ...paymentForm, scheduledPaymentDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
              placeholder="Optional notes about the payment"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessingPayroll(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submitProcessPayment}
            variant="contained"
            disabled={submitting || !paymentForm.paymentMethod}
          >
            {submitting ? <CircularProgress size={20} /> : 'Process Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Complete Payment Dialog */}
      <Dialog 
        open={!!completingPayroll} 
        onClose={() => setCompletingPayroll(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Complete Payment - {completingPayroll?.payrollNumber}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Box>
              <Typography variant="body1" gutterBottom>
                Staff: {completingPayroll?.staffName}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Net Amount: {completingPayroll && formatCurrency(completingPayroll.netAmount)}
              </Typography>
              <Typography variant="body1" gutterBottom>
                Payment Method: {completingPayroll?.paymentMethod}
              </Typography>
            </Box>
            <TextField
              fullWidth
              label="Transaction ID"
              value={completeForm.transactionId}
              onChange={(e) => setCompleteForm({ ...completeForm, transactionId: e.target.value })}
              required
              placeholder="e.g., Transaction confirmation number"
            />
            <TextField
              fullWidth
              label="Actual Payment Date"
              type="date"
              value={completeForm.actualPaymentDate}
              onChange={(e) => setCompleteForm({ ...completeForm, actualPaymentDate: e.target.value })}
              required
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompletingPayroll(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submitCompletePayment}
            variant="contained"
            disabled={submitting || !completeForm.transactionId || !completeForm.actualPaymentDate}
          >
            {submitting ? <CircularProgress size={20} /> : 'Complete Payment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PayrollView;